import {
  type BookSize,
  type BookTextStyle,
  type DesignedCoverId,
} from '@mixtiles/web-backend-shared'
import type { MotionValue } from 'motion/react'
import type { WebGLRenderer } from 'three'
import { type Photo, type PickedPhoto } from '../../PhotoBank/PhotoBank.types'
import { type Crop } from '../../../utils/cropUtils.types'
import { type HappyData } from 'services/faceDetection'
import { type UploadProgress } from '../../UploadProgressDialog/UploadProgressDialog'
import { type CustomPageMaterial } from '../pageMaterial'
import { type EditorDeviceProps } from 'features/photoBookEditorTelemetry/getEditorDeviceProps'
import { type CoverStyleId } from 'features/photoBookAiCovers/coverStyles.consts'

export type GpsCoordinates = {
  latitude: number
  longitude: number
}

export type PagePhoto = {
  uid: string // PhotoBank uid
  dateTaken?: number
  gps?: GpsCoordinates
  cropParams: Record<number, Crop>
  hasLowResWarn?: boolean
  lowQualityApproved?: boolean
  fixedIndex?: boolean
  appendedToEnd?: boolean
  happyData?: HappyData
  caption?: Caption
  doFacesOverflow?: boolean
}

export type PageSideContent = {
  photos: PagePhoto[]
  // can be layout, color, label in the future
}

export type PageIndex = {
  pageIndex: number
  pageSide: PageSide
}

export type CoverHighlightState = 'photo' | 'title' | null

// The cover drawer's view. `select` is the new drawer's Select Cover.
export type CoverPickerTab = 'select' | 'photo' | 'design' | 'generate'

// A cover picked in the new cover drawer and previewed on the book, not yet applied.
export type StagedCover =
  | { type: 'photo'; photo: PagePhoto }
  | { type: 'ai-generated'; aiCover: AiGeneratedCover }

// Tagged with its photo and slot so a zoomed-out page crop can't land on the
// cover: the crop tween keeps publishing after the page is unhighlighted.
export type PendingCrop = {
  crop: Crop
  photoUid: string
  target: 'cover' | 'page'
}
export type PageCaptionEditState = {
  pageIndex: number
  pageSide: PageSide
  selectionStart?: number
} | null

export enum PageSide {
  FRONT = 'front',
  BACK = 'back',
}

// Which way the book is closed, or that it is open on a spread. Navigation
// state, so it lives in the provider and never in the book's data.
export const BOOK_OPEN_STATES = {
  CLOSED_ON_FRONT: 'closed-on-front',
  OPEN: 'open',
  CLOSED_ON_BACK: 'closed-on-back',
} as const

export type BookOpenState =
  (typeof BOOK_OPEN_STATES)[keyof typeof BOOK_OPEN_STATES]

export type MaterialSet = {
  highRes?: { material: CustomPageMaterial; url: string }
  minRes?: { material: CustomPageMaterial; url: string }
}

export type Page = {
  front: PageSideContent
  back: PageSideContent
}

export type Caption = {
  // This parameter is sent to the backend as the font size in the pdf
  // it comes from the client since it involves frontend calculations that
  // are harder to do on the backend
  fontHeight?: number
  text: string
  style: BookTextStyle
}

type BaseCover = {
  caption: Caption
}

export type PhotoCover = BaseCover & {
  type: 'photo'
  photo: PagePhoto
}

export type DesignedCover = BaseCover & {
  type: 'designed'
  designedCoverId: DesignedCoverId
}

export type AiGeneratedCover = BaseCover & {
  type: 'ai-generated'
  coverUrl: string
  backgroundColor?: string
  textColor?: string
  // The headline the user supplied to generate this cover; persisted so order
  // analytics can report it even after the book caption is edited.
  aiGeneratedTitle?: string
  // Absent on covers saved before styles existed; those show and print as Iconic.
  style?: CoverStyleId
}

// A generated cover in use as the book's cover. Its caption is the headline the
// backend prints on the spine, so the book's own title is kept beside it for
// when the book goes back to a photo or designed cover.
export type AiGeneratedBookCover = AiGeneratedCover & {
  bookCaption: Caption
}

export type Cover = PhotoCover | DesignedCover | AiGeneratedBookCover

// What the cover of a book should be at creation, decided before any photo is
// uploaded. Resolved into a Cover once the book's photos exist.
export type InitialCoverRequest =
  | { kind: 'best-photo' }
  | { kind: 'gps-designed' }
  | { kind: 'photo-url'; url: string }

export const BEST_PHOTO_COVER_REQUEST: InitialCoverRequest = {
  kind: 'best-photo',
}

// The requests that fully describe a cover on their own, so a book can be born
// from one without a single photo — never resolved against uploaded photos.
export type StartBookCoverRequest =
  | { kind: 'designed'; designedCoverId: DesignedCoverId }
  | {
      kind: 'ai-generated'
      coverUrl: string
      backgroundColor: string
      textColor: string
      aiGeneratedTitle?: string
    }

export interface CurrentPageHover {
  pageSide: PageSide
  pageIndex: number
}

export interface FlipPageParams {
  closeCover: boolean
  source:
    | 'Swipe'
    | 'Arrow Key'
    | 'Arrow Button'
    | 'Moving Page'
    | 'Arrange Tutorial'
  // The DOM event's timeStamp, so a press that queued behind a frozen main
  // thread is timed from when the user made it, not from when it was handled.
  inputTimeStamp: number
}

export type FlipAnimation =
  | { kind: 'page'; pageIndex: number }
  | { kind: 'cover' }

export type PointerPosition = {
  x: number
  y: number
}

export type BookMotionRect = {
  top: MotionValue<number>
  bottom: MotionValue<number>
  left: MotionValue<number>
  right: MotionValue<number>
  middleY: MotionValue<number>
  middleX: MotionValue<number>
  leftPageMiddleX: MotionValue<number>
  rightPageMiddleX: MotionValue<number>

  width: MotionValue<number>
  height: MotionValue<number>
  cameraX: MotionValue<number>
  cameraY: MotionValue<number>
  worldUnitsPerPixel: MotionValue<number>
}

export enum PhotoBookOrderValidation {
  VALID = 'valid',
  NOT_ENOUGH_PHOTOS = 'not_enough_photos',
}

export interface PhotoBookContextType {
  bookSize: BookSize
  setBookSize: (size: BookSize) => void
  cover: Cover | null
  setCoverPhoto: (photo: PagePhoto) => void
  updateCoverCaption: (caption: Partial<Caption>) => void
  pages: Page[]
  updatePages: (pages: Page[]) => void
  addPhotos: (
    files: PickedPhoto[],
    cloudServiceName: string | null,
    shouldOrderPhotos: boolean,
    initialCover?: InitialCoverRequest
  ) => Promise<void>
  replacePhoto: (
    file: PickedPhoto,
    cloudServiceName: string | null,
    page: PageIndex
  ) => void
  flipRight: (params: FlipPageParams) => void
  flipLeft: (params: FlipPageParams) => void
  markFlipAnimationSettled: (animation: FlipAnimation) => void
  markEditorRendererCreated: (renderer: WebGLRenderer) => void
  getCachedEditorDeviceProps: () => EditorDeviceProps
  highlightedPageIndex: number | null
  highlightedPageSide: PageSide | null
  highlightPage: (pageIndex: number | null) => void
  coverHighlightState: CoverHighlightState
  isCoverHighlighted: boolean
  highlightCover: (highlightState: CoverHighlightState) => void
  openCoverDrawer: () => void
  coverDrawerOpenCount: number
  selectCoverSheetHeight: number | null
  recordSelectCoverSheetHeight: (height: number) => void
  showCoverDrawerView: (view: CoverPickerTab) => void
  currentPageIndex: number
  previousPageIndex: number
  isBookInitialized: boolean
  photosPrefetched: boolean
  updatePendingCrop: (pendingCrop: PendingCrop) => void
  pendingCrop: PendingCrop | null
  deletePage: (pageIndex: number, pageSide: PageSide) => void
  goToCover: () => Promise<void>
  goToPage: (pageIndex: PageIndex) => void
  setPhotoAsCover: (photo: string | PagePhoto) => void
  updatePagePhotoText: (
    pageIndex: number,
    pageSide: PageSide,
    photoIndex: number,
    text: string
  ) => void
  updatePhotoRendered: (uid: string) => void
  // Blob URLs of the min-res textures loaded for this session, by photo uid.
  // Stable identity: mutated in place, read by the renderer once pages land.
  localMinResUrlsByUid: Map<string, string>
  isSwiping: boolean
  // Pages mid-turn, from the first frame a flip or drag moves them until they come to rest
  turningPageIndices: ReadonlySet<number>
  markPageTurnStarted: (pageIndex: number) => void
  markPageTurnFinished: (pageIndex: number) => void
  // From a page change until the pages it turns have mounted and queued their turns
  isPageJumpStarting: boolean
  markPageJumpQueued: (pageIndex: number) => void
  deleteBook: () => void
  addingPhotosProgress: UploadProgress
  showCover: () => Promise<void>
  openCoverDrawerWithStagedCover: (stagedCover: StagedCover) => Promise<void>
  orderValidationStatus: PhotoBookOrderValidation
  clearOrderValidationWarning: () => void
  validatePhotoBookOrder: (updateState?: boolean) => PhotoBookOrderValidation
  bookOpenState: BookOpenState
  isBookClosedOnFront: boolean
  isBookClosedOnBack: boolean
  isCoverFrontOpen: boolean
  isCoverBackOpen: boolean
  isBookOpened: boolean
  isBookAnimating: boolean
  currentPageHover: CurrentPageHover | undefined
  setCurrentPageHover: React.Dispatch<
    React.SetStateAction<CurrentPageHover | undefined>
  >
  resetBookNavigationState: () => void
  isRenderPaused: boolean
  setIsRenderPaused: React.Dispatch<React.SetStateAction<boolean>>
  desktopSliderValue: number | undefined
  setDesktopSliderValue: React.Dispatch<
    React.SetStateAction<number | undefined>
  >
  bookMotionRect: BookMotionRect
  showInitialHint: boolean
  showKeyboardHint: boolean
  photosInChronologicalOrder: boolean
  setShowInitialHint: (show: boolean) => void
  setShowKeyboardHint: (show: boolean) => void
  globalPointerDown: boolean
  setGlobalPointerDown: React.Dispatch<React.SetStateAction<boolean>>
  lowResPhotos: Photo[]
  approveLowResPhotos: (uids: string[]) => void
  isZooming: boolean
  setIsZooming: (isZooming: boolean) => void
  pageToDelete: { pageIndex: number; pageSide: PageSide } | null
  setPageToDelete: React.Dispatch<
    React.SetStateAction<{ pageIndex: number; pageSide: PageSide } | null>
  >
  setIsCropping: React.Dispatch<React.SetStateAction<boolean>>
  isCropping: boolean
  isCoverRendered: boolean
  setIsCoverRendered: React.Dispatch<React.SetStateAction<boolean>>
  preparePhotoBookForInitialization: () => {
    photosNotInBank: number
  }
  photoBooksQuantity: number
  setPhotoBooksQuantity: (quantity: number) => void
  clickedOnSlider: React.MutableRefObject<boolean>
  createPagePhoto: (
    bankPhoto: Photo,
    dateTaken: number | undefined,
    withHappyScore: boolean,
    gps?: GpsCoordinates
  ) => Promise<PagePhoto>
  pageCaptionEditState: PageCaptionEditState
  setPageCaptionEditState: React.Dispatch<
    React.SetStateAction<PageCaptionEditState>
  >
  coverSelectionStart: number | undefined
  setCoverSelectionStart: React.Dispatch<
    React.SetStateAction<number | undefined>
  >
  setDesignedCover: (coverId: DesignedCoverId | null) => void
  aiGeneratedCovers: AiGeneratedCover[]
  setAiGeneratedCover: (cover: AiGeneratedCover) => void
  addAiGenCatalogItem: (cover: AiGeneratedCover) => void
  selectAiGeneratedCover: (cover: AiGeneratedCover) => void
  stagedCover: StagedCover | null
  stageCover: (stagedCover: StagedCover) => void
  clearStagedCover: () => void
  applyStagedCover: () => void
  coverPickerTab: CoverPickerTab
  setCoverPickerTab: React.Dispatch<React.SetStateAction<CoverPickerTab>>
  pendingInitialCover: InitialCoverRequest | null
  clearPendingInitialCover: () => void
  startBookFromCover: (
    request: StartBookCoverRequest
  ) => Promise<DesignedCover | AiGeneratedBookCover | null>
}
